import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  ConfigProvider,
  Flex,
  Modal,
  Select,
  Table,
  Typography,
  Upload,
} from "antd";
import { DownloadOutlined, InboxOutlined } from "@ant-design/icons";
import { modalButtonsTheme, ModalHeader } from "components/modals/ModalChrome";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { saveBlobAsFile } from "shared/utils/download";
import type { Analysis, BuildResult, Issue, PackageMapping } from "migration";
import { formatPhone } from "shared/utils/phone";
import { importChunk } from "shared/utils/appVersion";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

/**
 * Value centred over its label, not left-aligned like the list-page InlineStat
 * strip: these labels run 5-17 characters against a 1-2 digit value, so a
 * shared left edge leaves the number stranded at the far end of its own label.
 */
const Stat = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "amber";
}) => (
  <Flex vertical gap={2} align="center">
    <span
      style={{
        fontSize: textPx(22),
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        color: tone === "amber" ? colors.amber : colors.ink,
      }}
    >
      {value}
    </span>
    <span style={{ fontSize: textPx(14), color: colors.ink2 }}>{label}</span>
  </Flex>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{ display: "block", fontSize: textPx(12), color: colors.ink3, margin: "18px 0 8px" }}
  >
    {children}
  </span>
);

const STEP_KEYS = ["step_1", "step_2", "step_3", "step_4"];

// Why a suggestion was pre-filled: a plain line under the dropdown, the same
// shape as the credit warning, so the column carries one kind of note instead
// of a chip and a sentence competing for the same slot. Navy states a fact,
// amber interrupts.
const BASIS_COLOR = { name: colors.ink3, credits: colors.navy } as const;

/**
 * The prerequisites are a SEQUENCE, not a warning, so they read as numbered
 * steps rather than the paragraph an info box used to bury them in. The order
 * is load-bearing: the catalogue has to exist before the files are dropped,
 * because the whole point is resolving old packages to real tier ids.
 */
const StepList = () => (
  <div
    style={{
      marginTop: 16,
      padding: "16px 18px",
      background: colors.bgSoft,
      border: `1px solid ${colors.line}`,
      borderRadius: 8,
    }}
  >
    <span
      style={{
        display: "block",
        fontSize: textPx(13),
        fontWeight: 600,
        color: colors.ink,
        marginBottom: 12,
      }}
    >
      {translate("migration.steps_title")}
    </span>
    <Flex vertical gap={10}>
      {STEP_KEYS.map((stepKey, index) => (
        <Flex key={stepKey} align="flex-start" gap={10}>
          <span
            style={{
              flexShrink: 0,
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: colors.navyTint,
              color: colors.navyDark,
              fontSize: textPx(12),
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {index + 1}
          </span>
          {/* 22px line-height matches the circle, so the first line of a
              wrapping step still sits level with its number. */}
          <span style={{ fontSize: textPx(13), color: colors.ink2, lineHeight: `${textPx(22)}px` }}>
            {translate(`migration.${stepKey}`)}
          </span>
        </Flex>
      ))}
    </Flex>
  </div>
);

/**
 * Builds the Klien import file for a studio moving over from another platform.
 *
 * Three steps: drop the files in, confirm which Spott package each old
 * package became, download. The mapping step exists because the source
 * catalogue is flat (4x/8x/12x session are separate packages) while Spott
 * groups them as tiers of one product, so the studio's product name usually
 * matches none of the old names, and inferring it would attach clients to the
 * wrong package silently. Suggestions pre-fill every dropdown, so an admin who
 * kept the old names just confirms and moves on.
 *
 * Runs ENTIRELY in the browser: files are read locally, the converter is a lazy
 * chunk, nothing is uploaded.
 */
const DataMigrationModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  const [built, setBuilt] = useState<BuildResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setAnalysis(null);
    setMapping({});
    setBuilt(null);
    setError(null);
    setLoading(false);
  };

  const run = async (files: File[]) => {
    setLoading(true);
    setError(null);
    try {
      const payload = await Promise.all(
        files.map(async (file) => ({ name: file.name, text: await file.text() })),
      );
      const { analyzeExport } = await importChunk(() => import("migration"));
      const result = analyzeExport(payload);
      setAnalysis(result);
      // Suggestions become the starting mapping, so a clean run needs no edits.
      setMapping(
        Object.fromEntries(result.packages.map((p) => [p.key, p.suggestedTierId])),
      );
      setBuilt(null);
    } catch {
      setError(translate("migration.error"));
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  };

  const build = async () => {
    if (!analysis) return;
    const { buildKlienImport } = await importChunk(() => import("migration"));
    setBuilt(buildKlienImport(analysis, mapping));
  };

  const download = (name: string, content: string) =>
    saveBlobAsFile(new Blob([content], { type: "text/csv;charset=utf-8" }), name);

  const tierOptions = useMemo(
    () =>
      (analysis?.tiers ?? []).map((tier) => ({
        value: tier.id,
        label: tier.unlimited
          ? `${tier.product} (membership)`
          : `${tier.product} · ${tier.credits} ${translate("migration.credits_unit")}`,
      })),
    [analysis],
  );

  const tierById = useMemo(
    () => new Map((analysis?.tiers ?? []).map((tier) => [tier.id, tier])),
    [analysis],
  );

  const mappedCount = analysis
    ? analysis.packages.filter((p) => mapping[p.key]).length
    : 0;

  const mappingColumns = [
    {
      title: translate("migration.col_source_package"),
      dataIndex: "name",
      render: (value: string, row: PackageMapping) => (
        <Flex vertical>
          <span>{value}</span>
          <span style={{ fontSize: textPx(12), color: colors.ink3 }}>
            {row.unlimited
              ? translate("migration.membership_label")
              : `${row.credits} ${translate("migration.sessions")}`}
            {` · ${row.clientCount} ${translate("migration.clients_lower")}`}
          </span>
        </Flex>
      ),
    },
    {
      title: translate("migration.col_spott_package"),
      dataIndex: "key",
      width: 320,
      render: (packageKey: string, row: PackageMapping) => (
        <Flex vertical gap={4}>
          <Select
            style={{ width: "100%" }}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={translate("migration.select_placeholder")}
            value={mapping[packageKey] ?? undefined}
            options={tierOptions}
            onChange={(value) =>
              setMapping((prev) => ({ ...prev, [packageKey]: value ?? null }))
            }
          />
          {mapping[packageKey] &&
            mapping[packageKey] === row.suggestedTierId &&
            row.basis !== "none" && (
              <span style={{ fontSize: textPx(12), color: BASIS_COLOR[row.basis] }}>
                {translate(
                  row.basis === "name"
                    ? "migration.basis_name"
                    : "migration.basis_credits",
                )}
              </span>
            )}
          {/* The one thing worth interrupting for: no tier carries this credit
              count, so no choice here can make the numbers line up. */}
          {row.creditsUnmatched && (
            <span style={{ fontSize: textPx(12), color: colors.amberDark }}>
              {translate("migration.credits_unmatched")}
            </span>
          )}
          {/* A client holds more credits on this package than the chosen
              tier has: that row would fail at import, and one failed row
              rolls back the whole file (System Review C19). */}
          {(() => {
            const chosen = tierById.get(mapping[packageKey] ?? "");
            return chosen &&
              !chosen.unlimited &&
              chosen.credits !== null &&
              row.maxBalance !== null &&
              row.maxBalance > chosen.credits ? (
              <span style={{ fontSize: textPx(12), color: colors.amberDark }}>
                {translateWithVars("migration.balance_exceeds_tier", {
                  balance: row.maxBalance,
                  credits: chosen.credits,
                })}
              </span>
            ) : null;
          })()}
        </Flex>
      ),
    },
  ];

  const issueColumns = [
    {
      title: translate("migration.col_client"),
      dataIndex: "client",
      render: (value: string, row: Issue) => (
        <Flex vertical>
          <span>{value || "-"}</span>
          <span style={{ fontSize: textPx(12), color: colors.ink3 }}>{formatPhone(row.phone)}</span>
        </Flex>
      ),
    },
    {
      title: translate("migration.col_package"),
      dataIndex: "package",
      render: (value: string, row: Issue) => (
        <Flex vertical>
          <span>{value || "-"}</span>
          {row.remaining && (
            <span style={{ fontSize: textPx(12), color: colors.ink3 }}>
              {`${row.remaining} ${translate("migration.sessions_left")}`}
            </span>
          )}
        </Flex>
      ),
    },
    {
      title: translate("migration.col_problem"),
      dataIndex: "problem",
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterClose={reset}
      width={900}
      destroyOnClose
      title={<ModalHeader title={translate("migration.modal_title")} />}
      footer={
        <ConfigProvider theme={modalButtonsTheme}>
          <Flex justify="end" gap={10}>
            {analysis && <Button onClick={reset}>{translate("migration.reset")}</Button>}
            {analysis?.ready && !built && (
              <Button type="primary" onClick={build}>
                {translate("migration.build")}
              </Button>
            )}
            {(!analysis || built) && (
              <Button type="primary" onClick={onClose}>
                {translate("button.done")}
              </Button>
            )}
          </Flex>
        </ConfigProvider>
      }
    >
      <Text style={{ display: "block", color: colors.ink2, fontSize: textPx(13) }}>
        {translate("migration.modal_desc")}
      </Text>

      {!analysis && (
        <>
          {/* Shown before file selection: learning the order from an error
              afterwards would be worse. */}
          <StepList />
          <div style={{ marginTop: 16 }}>
            <Upload.Dragger
              multiple
              accept=".csv"
              showUploadList={false}
              disabled={loading}
              // Returning false keeps AntD from POSTing: the files are consumed
              // locally and never leave the browser.
              beforeUpload={(_file, fileList) => {
                if (fileList.length > 0) void run(fileList as File[]);
                return false;
              }}
            >
              <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                <InboxOutlined style={{ color: colors.navy }} />
              </p>
              <p style={{ fontSize: textPx(14), color: colors.ink }}>
                {loading ? translate("migration.working") : translate("migration.dropzone")}
              </p>
              <p style={{ fontSize: textPx(12), color: colors.ink3 }}>
                {translate("migration.dropzone_hint")}
              </p>
            </Upload.Dragger>
          </div>
        </>
      )}

      {error && <Alert type="error" showIcon message={error} style={{ marginTop: 16 }} />}

      {analysis && !analysis.ready && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message={translate("migration.missing_title")}
          description={
            <Flex vertical gap={2}>
              {analysis.missingRequired.map((name) => (
                <span key={name} style={{ fontSize: textPx(13) }}>
                  {name}
                </span>
              ))}
              <span style={{ fontSize: textPx(13), marginTop: 6 }}>
                {translate("migration.missing_hint")}
              </span>
            </Flex>
          }
        />
      )}

      {/* What we read on the left, what it adds up to on the right: one band,
          so the counts are read against the files that produced them instead
          of as a separate scoreboard further down. */}
      {analysis && (
        <>
          <SectionLabel>{translate("migration.recognized")}</SectionLabel>
          <Flex justify="space-between" align="flex-start" gap={32} wrap="wrap">
            <Flex vertical gap={4}>
              {analysis.reports.map((report) => (
                <span
                  key={report.label}
                  style={{
                    fontSize: textPx(13),
                    fontWeight: 500,
                    color: report.used ? colors.ink : colors.ink3,
                  }}
                >
                  {`${report.label}: ${report.rows} ${translate("migration.rows")}`}
                  {!report.used && ` (${translate("migration.not_needed")})`}
                </span>
              ))}
              {analysis.unrecognized.map((name) => (
                <span
                  key={name}
                  style={{ fontSize: textPx(13), fontWeight: 500, color: colors.ink3 }}
                >
                  {`${name}: ${translate("migration.unrecognized")}`}
                </span>
              ))}
            </Flex>

            {analysis.ready && (
              <Flex gap={36} wrap="wrap">
                <Stat
                  label={translate("migration.stat_clients")}
                  value={analysis.clientCount}
                />
                <Stat
                  label={translate("migration.stat_mapped")}
                  value={mappedCount}
                  tone={mappedCount < analysis.packages.length ? "amber" : undefined}
                />
                <Stat
                  label={translate("migration.stat_issues")}
                  value={analysis.issues.length}
                  tone={analysis.issues.length > 0 ? "amber" : undefined}
                />
              </Flex>
            )}
          </Flex>
        </>
      )}

      {analysis?.ready && (
        <>
          <SectionLabel>{translate("migration.mapping_title")}</SectionLabel>
          <Table
            size="small"
            rowKey="key"
            columns={mappingColumns}
            dataSource={analysis.packages}
            pagination={false}
          />

          {analysis.issues.length > 0 && (
            <>
              <SectionLabel>
                {`${translate("migration.issues_title")} (${analysis.issues.length})`}
              </SectionLabel>
              <Table
                size="small"
                rowKey={(row, index) => `${row.phone}-${index}`}
                columns={issueColumns}
                dataSource={analysis.issues}
                pagination={{ pageSize: 5, size: "small" }}
              />
            </>
          )}

          {/* Last thing on the page, after the review list: the downloads are
              the end of the job, not a step in the middle of it. */}
          {built && (
            <>
              <SectionLabel>{translate("migration.downloads")}</SectionLabel>
              <Flex gap={8} wrap="wrap">
                {built.files.map((file) => (
                  <Button
                    key={file.name}
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => download(file.name, file.content)}
                  >
                    {file.name}
                  </Button>
                ))}
              </Flex>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default DataMigrationModal;
